import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog.js';
import { Button } from './ui/button.js';
import { Input } from './ui/input.js';
import { Label } from './ui/label.js';
import { Textarea } from './ui/textarea.js';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select.js';
import { Plus, Trash2, Save, Printer, X, Download } from 'lucide-react';
import { useToast } from '../hooks/use-toast.js';
import axios from 'axios';
import { resolveBackendBase } from '../utils/backendBase.js';
const API_URL = ("production" === 'production'
    ? '/api'
    : `${resolveBackendBase() || ''}/api`.replace('//api', '/api'));
const DocumentFormDialog = ({ isOpen, onClose, documentType, // 'diagnosis', 'invoice', 'quote', 'receipt'
vehicle, onSaved }) => {
    const { toast } = useToast();
    const [loading, setLoading] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [workshopProfile, setWorkshopProfile] = useState(null);
    // Form data
    const [formData, setFormData] = useState({
        title: '',
        notes: '',
        items: [],
        subtotal: 0,
        discount: 0,
        tax: 0,
        total: 0
    });
    // Inline preview (avoid nested Dialog portals)
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewHtml, setPreviewHtml] = useState('');
    const iframeRef = useRef(null);
    useEffect(() => {
        if (isOpen) {
            loadTemplates();
            loadWorkshopProfile();
            initializeForm();
        }
        else {
            // ensure child preview is closed to avoid portal removeChild errors
            if (previewOpen)
                setPreviewOpen(false);
        }
    }, [isOpen, documentType]);
    const loadWorkshopProfile = async () => {
        try {
            const res = await axios.get(`${API_URL}/profile`);
            setWorkshopProfile(res.data);
        }
        catch (e) {
            console.error('Failed to load workshop profile:', e);
        }
    };
    useEffect(() => {
        // write to iframe when previewOpen and html available
        if (previewOpen && iframeRef.current && previewHtml) {
            try {
                const doc = iframeRef.current.contentDocument || iframeRef.current.contentWindow.document;
                doc.open();
                doc.write(previewHtml);
                doc.close();
            }
            catch (e) {
                // ignore
            }
        }
    }, [previewOpen, previewHtml]);
    const loadTemplates = async () => {
        try {
            const res = await axios.get(`${API_URL}/templates`);
            const filtered = res.data.filter(t => t.type === documentType || t.type === 'generic' || t.isActive);
            setTemplates(filtered);
            const active = filtered.find(t => t.isActive && (t.type === documentType));
            if (active)
                setSelectedTemplate(active.id);
            else if (filtered.length > 0)
                setSelectedTemplate(filtered[0].id);
        }
        catch (e) {
            console.error('Failed to load templates:', e);
        }
    };
    const initializeForm = () => {
        const titles = {
            diagnosis: 'تقرير تشخيص المركبة',
            invoice: 'فاتورة',
            quote: 'عرض سعر',
            receipt: 'سند قبض'
        };
        setFormData({
            title: titles[documentType] || 'مستند',
            notes: '',
            items: vehicle?.services?.map(s => ({ name: s, quantity: 1, price: 0, total: 0 })) || [],
            subtotal: 0,
            discount: 0,
            tax: 0,
            total: 0
        });
    };
    const addItem = () => {
        setFormData(prev => ({
            ...prev,
            items: [...prev.items, { name: '', quantity: 1, price: 0, total: 0 }]
        }));
    };
    const removeItem = (index) => {
        setFormData(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
        recalculateTotals();
    };
    const updateItem = (index, field, value) => {
        const newItems = [...formData.items];
        newItems[index][field] = value;
        if (field === 'quantity' || field === 'price') {
            const qty = parseFloat(newItems[index].quantity) || 0;
            const price = parseFloat(newItems[index].price) || 0;
            newItems[index].total = qty * price;
        }
        setFormData(prev => ({ ...prev, items: newItems }));
        recalculateTotals(newItems);
    };
    const recalculateTotals = (items = formData.items) => {
        const subtotal = items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);
        const discount = parseFloat(formData.discount) || 0;
        const taxRate = 0.15;
        const afterDiscount = subtotal - discount;
        const tax = afterDiscount * taxRate;
        const total = afterDiscount + tax;
        setFormData(prev => ({
            ...prev,
            subtotal: subtotal.toFixed(2),
            tax: tax.toFixed(2),
            total: total.toFixed(2)
        }));
    };
    const handleSave = async () => {
        if (documentType === 'invoice') {
            toast({
                title: 'إصدار الفاتورة من الزيارة',
                description: 'أصدر الفاتورة من بطاقة الزيارة بعد اعتمادها المالي.',
                variant: 'destructive',
            });
            return;
        }
        if (!formData.title.trim()) {
            toast({ title: 'خطأ', description: 'العنوان مطلوب', variant: 'destructive' });
            return;
        }
        if (formData.items.length === 0) {
            toast({ title: 'خطأ', description: 'أضف بند واحد على الأقل', variant: 'destructive' });
            return;
        }
        try {
            setLoading(true);
            const payload = {
                vehicleId: vehicle?.id,
                customerId: vehicle?.customerId,
                title: formData.title,
                notes: formData.notes,
                items: formData.items,
                subtotal: parseFloat(formData.subtotal),
                discount: parseFloat(formData.discount),
                tax: parseFloat(formData.tax),
                total: parseFloat(formData.total),
                templateId: selectedTemplate
            };
            let endpoint = '';
            if (documentType === 'diagnosis')
                endpoint = '/diagnosis-cases';
            else if (documentType === 'invoice')
                endpoint = '/invoices';
            else if (documentType === 'quote')
                endpoint = '/quotes';
            else if (documentType === 'receipt') {
                endpoint = '/customer-receipts';
                payload.amount = parseFloat(formData.total);
            }
            const res = await axios.post(`${API_URL}${endpoint}`, payload);
            toast({ title: 'تم الحفظ', description: 'تم حفظ المستند بنجاح' });
            if (onSaved)
                onSaved(res.data);
            await handlePreview();
        }
        catch (e) {
            toast({ title: 'خطأ', description: 'فشل في حفظ المستند', variant: 'destructive' });
            console.error(e);
        }
        finally {
            setLoading(false);
        }
    };
    const handlePreview = async () => {
        if (documentType === 'invoice') {
            toast({
                title: 'معاينة الفاتورة غير متاحة هنا',
                description: 'استخدم بطاقة الزيارة النهائية لعرض الفاتورة القانونية.',
                variant: 'destructive',
            });
            return;
        }
        try {
            setLoading(true);
            if (selectedTemplate) {
                try {
                    await axios.post(`${API_URL}/print/resolve-template`, { override_type: documentType, template_id: selectedTemplate });
                }
                catch (e) { /* ignore */ }
            }
            const renderData = {
                override_type: documentType,
                template_id: selectedTemplate,
                data: {
                    WORKSHOP_NAME: workshopProfile?.name || 'ورشتي',
                    WORKSHOP_NAME_EN: workshopProfile?.nameEnglish || '',
                    WORKSHOP_ADDRESS: workshopProfile?.address || '',
                    WORKSHOP_PHONE: workshopProfile?.phone || '',
                    WORKSHOP_EMAIL: workshopProfile?.email || '',
                    WORKSHOP_CITY: workshopProfile?.city || '',
                    TAX_NUMBER: workshopProfile?.taxNumber || '',
                    COMMERCIAL_REGISTER: workshopProfile?.commercialRegister || '',
                    CUSTOMER_NAME: vehicle?.customerName,
                    CUSTOMER_PHONE: vehicle?.customerPhone,
                    CUSTOMER_BALANCE: '0.00',
                    VEHICLE_PLATE: vehicle?.plateNumber,
                    VEHICLE_MODEL: `${vehicle?.brand || ''} ${vehicle?.model || ''}`,
                    VEHICLE_YEAR: vehicle?.year,
                    DOCUMENT_TITLE: formData.title,
                    DOCUMENT_NOTES: formData.notes,
                    FOOTER_NOTES: formData.notes,
                    INVOICE_NO: `INV-${Date.now().toString().slice(-6)}`,
                    items: formData.items,
                    SUBTOTAL: formData.subtotal,
                    DISCOUNT: formData.discount,
                    TAX: formData.tax,
                    TOTAL: formData.total,
                    DATE: new Date().toLocaleDateString('ar-SA')
                }
            };
            const res = await axios.post(`${API_URL}/print/render`, renderData);
            setPreviewHtml(res.data.html || res.data?.html || res.data);
            setPreviewOpen(true);
        }
        catch (e) {
            toast({ title: 'خطأ', description: 'فشل في إنشاء المعاينة', variant: 'destructive' });
            console.error(e);
        }
        finally {
            setLoading(false);
        }
    };
    const docTitles = {
        diagnosis: 'تقرير التشخيص',
        invoice: 'الفاتورة',
        quote: 'عرض السعر',
        receipt: 'سند القبض'
    };
    const handleDialogOpenChange = (v) => {
        if (!v) {
            if (previewOpen)
                setPreviewOpen(false);
            setTimeout(() => onClose?.(false), 0);
        }
    };
    const downloadHtml = () => {
        try {
            const blob = new Blob([previewHtml || ''], { type: 'text/html;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${docTitles[documentType] || 'document'}.html`;
            a.click();
            URL.revokeObjectURL(url);
        }
        catch (e) {
            // Ignore download errors
        }
    };
    return (_jsx(_Fragment, { children: _jsx(Dialog, { open: isOpen, onOpenChange: handleDialogOpenChange, children: _jsxs(DialogContent, { className: "max-w-4xl max-h-[90vh] overflow-y-auto", dir: "rtl", children: [_jsxs(DialogHeader, { children: [_jsxs(DialogTitle, { className: "text-2xl", children: ["\u0625\u0646\u0634\u0627\u0621 ", docTitles[documentType]] }), _jsx(DialogDescription, { children: "\u0627\u0645\u0644\u0623 \u0627\u0644\u0628\u064A\u0627\u0646\u0627\u062A \u0627\u0644\u0645\u0637\u0644\u0648\u0628\u0629" })] }), previewOpen && (_jsx("div", { className: "fixed inset-0 bg-black/20 z-[60] flex items-center justify-center", children: _jsxs("div", { className: "bg-white rounded-lg shadow-xl w-[900px] max-w-[95vw] max-h-[90vh] overflow-hidden flex flex-col", children: [_jsxs("div", { className: "p-3 flex items-center justify-between border-b", children: [_jsxs("div", { className: "font-bold", children: ["\u0645\u0639\u0627\u064A\u0646\u0629 \u2014 ", docTitles[documentType]] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsxs(Button, { size: "sm", variant: "outline", onClick: downloadHtml, children: [_jsx(Download, { size: 16, className: "ml-1" }), "\u062A\u062D\u0645\u064A\u0644 HTML"] }), _jsx(Button, { size: "sm", onClick: () => {
                                                        try {
                                                            const w = iframeRef.current?.contentWindow;
                                                            w?.focus();
                                                            w?.print();
                                                        }
                                                        catch (e) {
                                                            // Ignore print errors
                                                        }
                                                    }, children: "\u0637\u0628\u0627\u0639\u0629" }), _jsxs(Button, { size: "sm", variant: "destructive", onClick: () => setPreviewOpen(false), children: [_jsx(X, { size: 16, className: "ml-1" }), "\u0625\u063A\u0644\u0627\u0642"] })] })] }), _jsx("div", { className: "flex-1", children: _jsx("iframe", { ref: iframeRef, title: "doc-preview", style: { width: '100%', height: '100%', border: 0 } }) })] }) })), _jsxs("div", { className: "space-y-6 py-4", children: [_jsxs("div", { className: "bg-slate-50 p-4 rounded-lg", children: [_jsx("h3", { className: "font-bold text-slate-800 mb-2", children: vehicle?.plateNumber }), _jsxs("p", { className: "text-sm text-slate-600", children: [vehicle?.brand, " ", vehicle?.model, " - ", vehicle?.year] }), _jsx("p", { className: "text-sm text-slate-600", children: vehicle?.customerName })] }), _jsxs("div", { children: [_jsx(Label, { children: "\u0627\u062E\u062A\u0631 \u0627\u0644\u0646\u0645\u0648\u0630\u062C" }), _jsxs(Select, { value: selectedTemplate, onValueChange: setSelectedTemplate, children: [_jsx(SelectTrigger, { children: _jsx(SelectValue, { placeholder: "\u0627\u062E\u062A\u0631 \u0646\u0645\u0648\u0630\u062C \u0627\u0644\u0637\u0628\u0627\u0639\u0629" }) }), _jsx(SelectContent, { children: templates.map(t => (_jsx(SelectItem, { value: t.id, children: t.name }, t.id))) })] })] }), _jsxs("div", { children: [_jsx(Label, { children: "\u0627\u0644\u0639\u0646\u0648\u0627\u0646" }), _jsx(Input, { value: formData.title, onChange: e => setFormData(prev => ({ ...prev, title: e.target.value })), placeholder: "\u0639\u0646\u0648\u0627\u0646 \u0627\u0644\u0645\u0633\u062A\u0646\u062F" })] }), _jsxs("div", { children: [_jsxs("div", { className: "flex justify-between items-center mb-3", children: [_jsx(Label, { className: "text-lg", children: "\u0627\u0644\u0628\u0646\u0648\u062F" }), _jsxs(Button, { onClick: addItem, size: "sm", variant: "outline", children: [_jsx(Plus, { size: 16, className: "ml-1" }), "\u0625\u0636\u0627\u0641\u0629 \u0628\u0646\u062F"] })] }), _jsx("div", { className: "space-y-2", children: formData.items.map((item, idx) => (_jsxs("div", { className: "grid grid-cols-12 gap-2 items-end p-3 bg-slate-50 rounded", children: [_jsxs("div", { className: "col-span-5", children: [_jsx(Label, { className: "text-xs", children: "\u0627\u0644\u0627\u0633\u0645" }), _jsx(Input, { value: item.name, onChange: e => updateItem(idx, 'name', e.target.value), placeholder: "\u0627\u0633\u0645 \u0627\u0644\u062E\u062F\u0645\u0629/\u0627\u0644\u062C\u0632\u0621" })] }), _jsxs("div", { className: "col-span-2", children: [_jsx(Label, { className: "text-xs", children: "\u0627\u0644\u0643\u0645\u064A\u0629" }), _jsx(Input, { type: "number", value: item.quantity, onChange: e => updateItem(idx, 'quantity', e.target.value) })] }), _jsxs("div", { className: "col-span-2", children: [_jsx(Label, { className: "text-xs", children: "\u0627\u0644\u0633\u0639\u0631" }), _jsx(Input, { type: "number", value: item.price, onChange: e => updateItem(idx, 'price', e.target.value) })] }), _jsxs("div", { className: "col-span-2", children: [_jsx(Label, { className: "text-xs", children: "\u0627\u0644\u0645\u062C\u0645\u0648\u0639" }), _jsx(Input, { type: "number", value: item.total, disabled: true, className: "bg-slate-100" })] }), _jsx("div", { className: "col-span-1", children: _jsx(Button, { variant: "destructive", size: "sm", onClick: () => removeItem(idx), children: _jsx(Trash2, { size: 16 }) }) })] }, idx))) })] }), _jsxs("div", { className: "bg-slate-50 p-4 rounded-lg space-y-2", children: [_jsxs("div", { className: "flex justify-between", children: [_jsx("span", { children: "\u0627\u0644\u0645\u062C\u0645\u0648\u0639 \u0627\u0644\u0641\u0631\u0639\u064A:" }), _jsxs("span", { className: "font-bold", children: [formData.subtotal, " \u0631.\u0633"] })] }), _jsxs("div", { className: "flex justify-between items-center", children: [_jsx("span", { children: "\u0627\u0644\u062E\u0635\u0645:" }), _jsx(Input, { type: "number", value: formData.discount, onChange: e => {
                                                    setFormData(prev => ({ ...prev, discount: e.target.value }));
                                                    recalculateTotals();
                                                }, className: "w-32 text-right" })] }), _jsxs("div", { className: "flex justify-between", children: [_jsx("span", { children: "\u0627\u0644\u0636\u0631\u064A\u0628\u0629 (15%):" }), _jsxs("span", { className: "font-bold", children: [formData.tax, " \u0631.\u0633"] })] }), _jsxs("div", { className: "flex justify-between text-xl border-t pt-2", children: [_jsx("span", { className: "font-bold", children: "\u0627\u0644\u0625\u062C\u0645\u0627\u0644\u064A:" }), _jsxs("span", { className: "font-bold text-green-600", children: [formData.total, " \u0631.\u0633"] })] })] }), _jsxs("div", { children: [_jsx(Label, { children: "\u0645\u0644\u0627\u062D\u0638\u0627\u062A" }), _jsx(Textarea, { value: formData.notes, onChange: e => setFormData(prev => ({ ...prev, notes: e.target.value })), placeholder: "\u0645\u0644\u0627\u062D\u0638\u0627\u062A \u0625\u0636\u0627\u0641\u064A\u0629...", rows: 3 })] }), _jsxs("div", { className: "flex gap-3", children: [_jsxs(Button, { onClick: handleSave, disabled: loading, className: "flex-1 bg-green-600 hover:bg-green-700", children: [_jsx(Save, { size: 18, className: "ml-2" }), "\u062D\u0641\u0638 \u0648\u0637\u0628\u0627\u0639\u0629"] }), _jsxs(Button, { onClick: () => handlePreview(), disabled: loading, variant: "outline", className: "flex-1", children: [_jsx(Printer, { size: 18, className: "ml-2" }), "\u0645\u0639\u0627\u064A\u0646\u0629 \u0641\u0642\u0637"] })] })] })] }) }) }));
};
export default DocumentFormDialog;
